import React, { createContext, useContext, useState, useEffect } from 'react'
import { api } from '../api/client'
import { useAuth } from './AuthContext'
import { toast } from 'react-hot-toast'
import { getApiErrorMessage } from '../shared/utils/apiError'

export type AttendanceStatus = 'not_checked_in' | 'on_duty' | 'break' | 'meeting' | 'checked_out'

interface AttendanceContextType {
  status: AttendanceStatus
  checkInTime: string | null
  workingHours: { start: string; end: string } | null
  isLoading: boolean
  checkIn: () => Promise<void>
  checkOut: () => Promise<void>
  changeStatus: (newStatus: AttendanceStatus) => Promise<void>
}

const AttendanceContext = createContext<AttendanceContextType | undefined>(undefined)

export function AttendanceProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, organization } = useAuth()
  const [status, setStatus] = useState<AttendanceStatus>('not_checked_in')
  const [checkInTime, setCheckInTime] = useState<string | null>(null)
  const [workingHours, setWorkingHours] = useState<{ start: string; end: string } | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (isAuthenticated && organization) {
      loadAttendance()
    } else {
      setIsLoading(false)
    }
  }, [isAuthenticated, organization])

  const loadAttendance = async () => {
    setIsLoading(true)
    try {
      const res = await api.get<any>('/attendance/today')
      if (res.workingHours) {
        setWorkingHours(res.workingHours)
      }
      if (res.attendance) {
        setStatus(res.attendance.status)
        setCheckInTime(res.attendance.checkInTime)
      } else {
        setStatus('not_checked_in')
      }
    } catch (err) {
      console.error('Failed to load attendance', err)
    } finally {
      setIsLoading(false)
    }
  }

  const checkIn = async () => {
    try {
      const res = await api.post<any>('/attendance/check-in', undefined, { skipErrorToast: true })
      setStatus(res.status)
      setCheckInTime(res.checkInTime)
      toast.success('Checked in successfully!')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'check in'))
    }
  }

  const checkOut = async () => {
    try {
      const res = await api.post<any>('/attendance/status', { status: 'checked_out' }, { skipErrorToast: true })
      setStatus(res.status)
      toast.success('Checked out successfully!')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'check out'))
    }
  }

  const changeStatus = async (newStatus: AttendanceStatus) => {
    if (newStatus === 'checked_out' || newStatus === 'not_checked_in') return
    try {
      const res = await api.post<any>('/attendance/status', { status: newStatus }, { skipErrorToast: true })
      setStatus(res.status)
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'update status'))
    }
  }

  const value = {
    status,
    checkInTime,
    workingHours,
    isLoading,
    checkIn,
    checkOut,
    changeStatus,
  }

  return <AttendanceContext.Provider value={value}>{children}</AttendanceContext.Provider>
}

export function useAttendance() {
  const context = useContext(AttendanceContext)
  if (!context) {
    throw new Error('useAttendance must be used within an AttendanceProvider')
  }
  return context
}
